import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { VACATION_CALENDAR } from "@/constants/testIds";

const WEEKDAY_LABELS = ["L", "M", "M", "J", "V", "S", "D"];
const MONTH_LABELS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function isoRange(start, end) {
  const dates = [];
  let d = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  while (d <= last) {
    dates.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

const pad = (n) => String(n).padStart(2, "0");

// Vista de calendario para Control de Vacaciones: además de la lista de solicitudes, un
// vistazo rápido de qué días tiene programados cada empleado — aprobados en verde, pendientes
// de aprobar o de confirmar en amarillo. No sustituye la lista, la complementa.
export function VacationCalendar({ requests }) {
  const [cursor, setCursor] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });

  const dayMap = useMemo(() => {
    const map = {};
    for (const r of requests) {
      if (r.status !== "approved" && r.status !== "pending" && r.status !== "awaiting_confirmation") continue;
      for (const day of isoRange(r.start_date, r.end_date)) {
        if (!map[day]) map[day] = [];
        map[day].push({ name: r.employee_name, status: r.status });
      }
    }
    return map;
  }, [requests]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7; // semana empieza en lunes
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div data-testid={VACATION_CALENDAR.container} className="bg-card border border-petroleum/10 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid={VACATION_CALENDAR.prevMonthButton}
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          className="p-1.5 rounded-full hover:bg-accent"
        >
          <ChevronLeft className="w-4 h-4 text-charcoal/70" />
        </button>
        <p className="font-heading font-semibold text-charcoal">{MONTH_LABELS[month]} {year}</p>
        <button
          data-testid={VACATION_CALENDAR.nextMonthButton}
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          className="p-1.5 rounded-full hover:bg-accent"
        >
          <ChevronRight className="w-4 h-4 text-charcoal/70" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-charcoal/50 mb-1">
        {WEEKDAY_LABELS.map((l, i) => <div key={i}>{l}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (day == null) return <div key={i} className="aspect-square" />;
          const iso = `${year}-${pad(month + 1)}-${pad(day)}`;
          const entries = dayMap[iso] || [];
          const hasApproved = entries.some((e) => e.status === "approved");
          const bg = hasApproved
            ? "bg-emerald-100 border-emerald-300"
            : entries.length > 0
              ? "bg-amber-100 border-amber-300"
              : "bg-white border-petroleum/10";
          return (
            <div
              key={i}
              data-testid={VACATION_CALENDAR.dayCell}
              title={entries.map((e) => `${e.name} (${e.status === "approved" ? "aprobada" : "por aprobar/confirmar"})`).join("\n")}
              className={`aspect-square rounded-lg border p-1 flex flex-col ${bg}`}
            >
              <span className="text-[11px] text-charcoal/60">{day}</span>
              {entries.length > 0 && (
                <div className="flex-1 flex flex-col justify-end gap-0.5 overflow-hidden">
                  {entries.slice(0, 2).map((e, idx) => (
                    <span
                      key={idx}
                      className={`text-[9px] leading-tight truncate px-1 rounded ${
                        e.status === "approved" ? "bg-emerald-600/20 text-emerald-800" : "bg-amber-600/20 text-amber-800"
                      }`}
                    >
                      {e.name.split(" ")[0]}
                    </span>
                  ))}
                  {entries.length > 2 && <span className="text-[9px] text-charcoal/40">+{entries.length - 2} más</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-4 text-xs text-charcoal/60">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300 inline-block" /> Aprobadas</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300 inline-block" /> Por aprobar / por confirmar</span>
      </div>
    </div>
  );
}
